import React, { useCallback, useMemo } from "react";
import { useAuthSession } from "../../auth/shared";
import {
  MerchantHomeInsightsSection,
  MerchantHomeStatsSection,
  MerchantHomeWelcomeSection,
  MerchantHomeTrendSection,
} from "../components";
import { useMerchantKeyInsights } from "../hooks/useMerchantKeyInsights";
import { useMerchantRedemptionsTrend } from "../hooks/useMerchantRedemptionsTrend";
import { useMerchantStatistics } from "../hooks/useMerchantStatistics";
import {
  getDisplayKeyInsights,
  getFormattedDate,
} from "../utils/merchantHomeViewModels";

const MerchantHomePage: React.FC = () => {
  const statistics = useMerchantStatistics();
  const trend = useMerchantRedemptionsTrend();
  const insights = useMerchantKeyInsights();
  const { user } = useAuthSession();

  const { retry: retryStatistics } = statistics;
  const { retry: retryTrend } = trend;
  const { retry: retryInsights } = insights;

  const isRefreshingAll = useMemo(
    () => statistics.refreshing || trend.refreshing || insights.refreshing,
    [statistics.refreshing, trend.refreshing, insights.refreshing],
  );

  const refreshAll = useCallback(async () => {
    await Promise.allSettled([retryStatistics(), retryTrend(), retryInsights()]);
  }, [retryStatistics, retryTrend, retryInsights]);

  const currentDate = getFormattedDate();
  const userName = user ? `${user.first_name} ${user.last_name}` : "Name";
  const displayInsights = getDisplayKeyInsights(insights.data);

  return (
    <div className="bg-[rgba(255,255,255,0.5)] dark:bg-gray-900/50 min-h-screen">
      <div className="bg-[#eef9ef]  dark:bg-gray-900/50 p-10">
        <div className="flex flex-col gap-10">
          <MerchantHomeStatsSection
            userName={userName}
            currentDate={currentDate}
            statistics={statistics.data}
            loading={statistics.loading}
            refreshing={statistics.refreshing || isRefreshingAll}
            error={statistics.error}
            onRetry={statistics.retry}
            onRefreshAll={refreshAll}
          />

          <MerchantHomeTrendSection
            trend={trend.data}
            loading={trend.loading}
            error={trend.error}
            onRetry={trend.retry}
          />

          <div className="self-stretch inline-flex justify-center items-start gap-6">
            <MerchantHomeInsightsSection
              insights={displayInsights}
              hasData={insights.data !== null}
              loading={insights.loading}
              error={insights.error}
              onRetry={insights.retry}
            />
            <MerchantHomeWelcomeSection
              userName={userName}
              currentDate={currentDate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantHomePage;
